/**
 * USE_RESOURCES.JS - Hook de gestion des ressources visuelles
 * 
 * Ce hook gère le chargement des ressources visuelles (textures, modèles 3D)
 * en utilisant les hooks optimisés de @react-three/drei.
 * 
 * FONCTIONNALITÉS:
 * - Préchargement global de toutes les ressources
 * - Chargement individuel dans les composants
 * - Cache automatique (Drei)
 * - Accès via Context API
 * 
 * UTILISATION:
 * - preloadResources() : Appelé au démarrage de l'app
 * - useResources() : Hook pour accéder aux ressources dans composants
 * 
 * NOTE: Les SONS ne sont PAS gérés ici !
 * Voir AudioProvider et useSounds pour la gestion audio.
 */

import { useContext, createContext } from 'react'
import { useGLTF, useTexture } from '@react-three/drei'
import { RESOURCES_CONFIG } from '../config/resources'

/**
 * Context pour partager les ressources
 * Fourni par ResourcesProvider
 */
export const ResourcesContext = createContext({
  items: {},
  loaded: false
})

/**
 * Préchargement global de toutes les ressources
 * 
 * Cette fonction doit être appelée AU DÉMARRAGE de l'application,
 * AVANT le premier rendu du Canvas.
 * 
 * Elle lance le préchargement de toutes les ressources en parallèle
 * via les hooks Drei (useGLTF.preload, useTexture.preload).
 * 
 * AVANTAGES:
 * - Pas de "flash" de chargement pendant navigation
 * - Cache Drei prêt dès le début
 * - Meilleure expérience utilisateur
 * 
 * EXEMPLE D'UTILISATION:
 * ```javascript
 * // Dans App.jsx, AVANT le return
 * preloadResources()
 * 
 * function App() {
 *   return <Canvas>...</Canvas>
 * }
 * ```
 */
export function preloadResources() {
  
  // Préchargement des matcaps (textures PNG)
  RESOURCES_CONFIG.matcaps.forEach((matcap) => {
    useTexture.preload(matcap.path)
  })
  
  // Préchargement des modèles 3D (GLB)
  RESOURCES_CONFIG.models.forEach((model) => {
    useGLTF.preload(model.path)
  })
  
  // Préchargement des textures (PNG)
  RESOURCES_CONFIG.textures.forEach((texture) => {
    useTexture.preload(texture.path)
  })

}

/**
 * Hook pour accéder aux ressources chargées
 * 
 * Utilise le Context fourni par ResourcesProvider pour accéder
 * aux ressources chargées dans toute l'application.
 * 
 * @returns {object} Objet contenant:
 *   - items: Dictionnaire des ressources chargées
 *   - loaded: Boolean indiquant si chargement terminé
 * 
 * EXEMPLE D'UTILISATION:
 * ```javascript
 * function MyComponent() {
 *   const { items, loaded } = useResources()
 *   
 *   if (!loaded) {
 *     return <Html>Chargement...</Html>
 *   }
 *   
 *   return <primitive object={items.house} />
 * }
 * ```
 */
export function useResources() {
  const context = useContext(ResourcesContext)
  
  if (!context) {
    throw new Error('useResources doit être utilisé dans un ResourcesProvider')
  }
  
  return context
}

/**
 * Hook pour charger un modèle 3D spécifique
 * 
 * Wrapper autour de useGLTF de Drei pour plus de commodité.
 * Utilise le cache si déjà préchargé.
 * 
 * @param {string} name - Nom du modèle dans RESOURCES_CONFIG
 * @returns {object} Résultat de useGLTF (scene, nodes, materials)
 * 
 * EXEMPLE D'UTILISATION:
 * ```javascript
 * function House() {
 *   const { scene } = useModel('house')
 *   return <primitive object={scene} />
 * }
 * ```
 */
export function useModel(name) {
  // Recherche du modèle dans la config
  const modelConfig = RESOURCES_CONFIG.models.find((m) => m.name === name)
  
  if (!modelConfig) {
    throw new Error(`Modèle "${name}" introuvable dans RESOURCES_CONFIG`)
  }
  
  // Chargement via Drei (cache automatique)
  return useGLTF(modelConfig.path)
}

/**
 * Hook pour charger une texture spécifique
 * 
 * Wrapper autour de useTexture de Drei pour plus de commodité.
 * Utilise le cache si déjà préchargée.
 * 
 * @param {string} name - Nom de la texture dans RESOURCES_CONFIG
 * @param {string} type - Type: 'matcap' ou 'texture' (défaut: 'texture')
 * @returns {THREE.Texture} Texture Three.js prête à l'emploi
 * 
 * EXEMPLE D'UTILISATION:
 * ```javascript
 * function MyMaterial() {
 *   const matcap = useTextureResource('matcapBeige', 'matcap')
 *   return <meshMatcapMaterial matcap={matcap} />
 * }
 * ```
 */
export function useTextureResource(name, type = 'texture') {
  // Sélection de la liste selon le type
  const list = type === 'matcap' 
    ? RESOURCES_CONFIG.matcaps 
    : RESOURCES_CONFIG.textures
  
  // Recherche de la texture dans la config
  const textureConfig = list.find((t) => t.name === name)
  
  if (!textureConfig) {
    throw new Error(
      `Texture "${name}" introuvable dans RESOURCES_CONFIG.${type}s`
    )
  }
  
  // Chargement via Drei (cache automatique)
  return useTexture(textureConfig.path)
}

/**
 * NOTES SUR LE SYSTÈME DE RESSOURCES:
 * 
 * 1. SÉPARATION AUDIO/VISUEL:
 *    - Ce système gère UNIQUEMENT les ressources visuelles
 *    - Les sons sont dans AudioProvider (Howler.js)
 * 
 * 2. PRÉCHARGEMENT:
 *    - Appeler preloadResources() au début de App.jsx
 *    - Lance le chargement en arrière-plan
 *    - Suspense gère l'affichage du loader
 * 
 * 3. CACHE DREI:
 *    - Drei met automatiquement en cache
 *    - Appels multiples = même ressource
 *    - Pas de duplication mémoire
 * 
 * 4. PERFORMANCE:
 *    - Préchargement parallèle
 *    - Compression Draco pour GLB
 *    - Textures optimisées
 */

